import { Suspense, lazy, useEffect } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import LoadingScreen from "./components/LoadingScreen";
import Home from "./components/home";
import { generateRoutes } from "./utils/routeGenerator";
import { tools } from "./data/tools";
import { getFeaturedTools } from "./lib/featured-utils";
import { AnimatePresence, motion } from "framer-motion";
import RouteValidator from "./components/dev/RouteValidator";

// Static pages (manually imported)
const About = lazy(() => import("./pages/AboutPage"));
const Contact = lazy(() => import("./pages/ContactPage"));
const NotFound = lazy(() => import("./components/NotFound"));
const SearchPage = lazy(() => import("./components/SearchPage"));
const CategoryPage = lazy(() => import("./components/CategoryPage"));
const AllToolsPage = lazy(() => import("./components/AllToolsPage"));

// Legal Pages
const Terms = lazy(() => import('./components/legal/Terms'));
const Privacy = lazy(() => import('./components/legal/Privacy'));
const Disclaimer = lazy(() => import('./components/legal/Disclaimer'));

// Programmatic Pages
const UnitConverter = lazy(() => import('./pages/UnitConverterPage'));
const ColorConverter = lazy(() => import('./pages/ColorConverterPage'));
const UnifiedImageConverter = lazy(() => import('./pages/UnifiedImageConverterPage'));
const ProgrammaticPage = lazy(() => import('./pages/ProgrammaticPage'));

import Layout from "./components/Layout";
import ScrollToTop from "./components/ScrollToTop";
import { Toaster } from "@/components/ui/toaster";
import { initGA, trackPageView } from "./lib/ga";

// Generate all tool routes dynamically from tools.ts outside the component
// to prevent recreating lazy components on every render (which causes blank screens).
const toolRoutes = generateRoutes();

function App() {
  console.log("📱 App component rendering...");
  const location = useLocation();

  // Initialize GA4 once on mount
  useEffect(() => {
    initGA();
  }, []);

  // Track pageviews on every route change
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  // Prefetch featured tools for faster navigation
  useEffect(() => {
    const featured = getFeaturedTools(tools);
    const timer = setTimeout(() => {
      featured.slice(0, 5).forEach(tool => {
        const route = toolRoutes.find(r => r.path === tool.route);
        if (route && typeof route.Component === 'function') {
          try {
            // @ts-ignore
            route.Component._preload?.();
          } catch (e) {
            // Fallback
          }
        }
      });
    }, 2000);
    return () => clearTimeout(timer);
  }, [toolRoutes]);

  return (
    <Layout>
      {import.meta.env.DEV && <RouteValidator />}
      <ScrollToTop />
      <Suspense fallback={<LoadingScreen />}>
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            <Routes location={location}>
              {/* Static Routes */}
              <Route path="/" element={<Home />} />
              <Route path="/all-tools" element={<AllToolsPage />} />
              <Route path="/category/:slug" element={<CategoryPage />} />

              {/* Legal Routes */}
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/disclaimer" element={<Disclaimer />} />

              {/* Dynamic Tool Routes */}
              {toolRoutes.map(({ path, Component }) => (
                <Route key={path} path={path} element={<Component />} />
              ))}

              {/* Programmatic Mass Routes — specific named patterns first */}

              {/* Programmatic wildcard catch-all — handled by ProgrammaticPage dispatcher */}
              <Route path="/converter/*" element={<ProgrammaticPage />} />
              <Route path="/text/*" element={<ProgrammaticPage />} />
              <Route path="/dev/*" element={<ProgrammaticPage />} />
              <Route path="/utility/*" element={<ProgrammaticPage />} />
              <Route path="/color/*" element={<ProgrammaticPage />} />
              <Route path="/image/*" element={<ProgrammaticPage />} />
              <Route path="/science/*" element={<ProgrammaticPage />} />
              <Route path="/pdf/*" element={<ProgrammaticPage />} />
              <Route path="/network/*" element={<ProgrammaticPage />} />
              <Route path="/seo/*" element={<ProgrammaticPage />} />
              <Route path="/calculator/*" element={<ProgrammaticPage />} />
              <Route path="/media/*" element={<ProgrammaticPage />} />
              <Route path="/games/*" element={<ProgrammaticPage />} />
              <Route path="/design/*" element={<ProgrammaticPage />} />
              <Route path="/explore/*" element={<ProgrammaticPage />} />
              <Route path="/earth-space/*" element={<ProgrammaticPage />} />

              {/* Search Page */}
              <Route path="/search" element={<SearchPage />} />

              {/* 404 Page */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </motion.div>
        </AnimatePresence>
      </Suspense>
    </Layout>
  );
}

export default App;
